import * as z from 'zod';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import languageListJson from './language-list.json';
import { getAudioDuration } from './audioHandler';
import { Charm_tickdouble, doubleTick, single_tick } from './images';
import { MESSAGE_URL_SPLIT_REGEX, isMessageURL, normalizeMessageURL } from './messageUrl';
import { MessageContentFormat, parseBasicFormat } from './basicMessageFormat';
const CryptoJS = require('crypto-js');
const socketIo = require('socket.io-client');

const oneDay = 24 * 60 * 60 * 1000;
export const expireTime = () => new Date(Date.now() + oneDay);

export const getAccessToken = () => cookieDecryption('userDetails')?.token;
export const isAuthenticated = () => !!getAccessToken();

export const Encryption = data => {
  try {
    if (data == null) {
      return undefined;
    }
    // CryptoJS expects a string (or WordArray). Objects used to throw here; the empty catch
    // then yielded undefined → socket.io emitted join with null and the server surfaced a
    // false "session expired" / token error (Messenger join handler).
    const plaintext = typeof data === 'string' ? data : JSON.stringify(data);
    return CryptoJS.AES.encrypt(plaintext, process.env.REACT_APP_SECRET_KEY).toString();
  } catch (e) {}
};

export function Decryption(data) {
  try {
    let bytes = CryptoJS.AES.decrypt(data, process.env.REACT_APP_SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (e) {}
}

// Unlike Decryption above, this one runs decodeURIComponent first — do not mix the two.
export const decryption = data => {
  let bytes = CryptoJS.AES.decrypt(decodeURIComponent(data), process.env.REACT_APP_SECRET_KEY);
  const cryptoBytes = bytes.toString(CryptoJS.enc.Utf8);
  let decryptedData = cryptoBytes ? JSON.parse(cryptoBytes) : null;
  return decryptedData;
};

export const cookieEncryption = (key, data) => {
  try {
    if (key && data) {
      let ciphertext = Encryption(JSON.stringify(data));
      Cookies.set(key, ciphertext, { path: '/', expires: expireTime() });
    }
  } catch (e) {}
};

export const cookieDecryption = key => {
  try {
    const data = Cookies.get(key);
    let decryptedData = null;
    if (key && data) {
      decryptedData = Decryption(data);
    }
    return decryptedData;
  } catch (e) {}
};

export function Success(msg) {
  toast.success(msg, {
    position: 'top-right',
    autoClose: 5000,
  });
}

export function Error(msg) {
  toast.error(msg, {
    position: 'top-right',
    autoClose: 5000,
  });
}

export const removeCookie = key => {
  Cookies.remove(key, { path: '/', expires: expireTime() });
};

export const onBoardingHandler = onboardingStep => {
  switch (onboardingStep) {
    case 0: {
      return '/signup-success';
    }
    case 1: {
      return '/basic-info';
    }
    case 2: {
      return '/additional-info';
    }
    case 3: {
      return '/matching-questions';
    }
    case 4: {
      return '/application-complete';
    }
    case 5: {
      return '/home';
    }
  }
};

export const stepByPath = path => {
  switch (path) {
    case '/signup-success': {
      return 0;
    }
    case '/basic-info': {
      return 1;
    }
    case '/additional-info': {
      return 2;
    }
    case '/matching-questions': {
      return 3;
    }
    case 4: {
      return '/application-complete';
    }
    case 5: {
      return '/home';
    }
  }
};

export const extractKeys = data => {
  const keys = [];

  const traverse = item => {
    if (item.key) {
      keys.push(item.key);
    }

    if (item.childrens && Array.isArray(item.childrens)) {
      item.childrens.forEach(traverse);
    }
  };

  data.forEach(traverse);
  return keys;
};

export const getFilteredCountry = () => {
  const filter = options => {
    return options?.filter(option => option?.cname?.toLowerCase());
  };

  const filteredOptions = filter(languageListJson).map(country => {
    return {
      value: country.iso,
      label: `${country.cname} (+${country.code})`,
      country: country,
      svg: country?.svg,
    };
  });

  return filteredOptions;
};
export const handleLogOut = () => {
  const socket = socketIo(process.env.REACT_APP_SOCKET_API_KEY, {
    transports: ['websocket'],
  });
  Cookies.remove('userDetails', { path: '/' });
  localStorage.setItem('userStatus', 'logged Out');
  socket?.close();
};

function buildFormData(formData, data, parentKey) {
  if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
    Object.keys(data).forEach(key => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data == null ? '' : data;

    formData.append(parentKey, value);
  }
}

export function jsonToFormData(data) {
  const formData = new FormData();
  buildFormData(formData, data);
  return formData;
}

export const getDuration = (duration, date) => {
  let seconds = duration.asSeconds();
  let minutes = duration.asMinutes();
  let hours = duration.asHours();
  let days = duration.asDays();
  let months = duration.asMonths();
  let years = duration.asYears();
  if (minutes <= 1440) {
    return `Today, ${UTCToPartnerTimeZone(date, { utc: 'UTC' }).format('h:mm A')}`;
  } else if (days < 2) {
    return `${days?.toString()?.split('.')[0]} Day, ${UTCToPartnerTimeZone(date, { utc: 'UTC' }).format('h:mm A')}`;
  } else if (days > 1) {
    return `${days?.toString()?.split('.')[0]} Days, ${UTCToPartnerTimeZone(date, { utc: 'UTC' }).format('h:mm A')}`;
  }
};

export const getDurationDate = (duration, date) => {
  let seconds = duration.asSeconds();
  let minutes = duration.asMinutes();
  let hours = duration.asHours();
  let days = duration.asDays();
  let months = duration.asMonths();
  let years = duration.asYears();

  if (minutes >= 525600) {
    return `${years.toFixed()} year ago`;
  } else if (months >= 1) {
    if (months >= 2) {
      return `${months.toFixed()} months ago`;
    }
    return `${months.toFixed()} month ago`;
  } else if (days < 2) {
    if (seconds < 60) {
      return 'now';
    } else if (minutes <= 1) {
      return `${minutes?.toString()?.split('.')[0]} min ago`;
    } else if (minutes > 1 && minutes < 60) {
      return `${minutes?.toString()?.split('.')[0]} mins ago`;
    } else if (minutes >= 60 && minutes < 120) {
      return `${hours?.toString()?.split('.')[0]} hour ago`;
    } else if (minutes >= 120 && minutes < 1440) {
      return `${hours?.toString()?.split('.')[0]} hours ago`;
    } else {
      return `${days?.toString()?.split('.')[0]} day ago`;
    }
  } else if (days > 1) {
    return `${days?.toString()?.split('.')[0]} days ago`;
  }
};

export function getTimeFormat(time, timezone) {
  let x = moment();
  let y = moment(time);
  let duration = moment.duration(x.diff(y));

  let x1 = moment().tz('UTC');
  let y1 = moment(time).tz(timezone?.utc)?.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  let duration1 = moment.duration(x1.diff(y1));
  let day = duration1.asDays();

  if (day < 1) {
    return UTCToPartnerTimeZone(time, timezone)?.format('hh:mm A');
  } else {
    return UTCToPartnerTimeZone(time, timezone)?.format('MM/DD/YYYY');
  }
}

export function getMessageTime(time, timezone) {
  const userTZ = timezone?.utc || 'UTC';
  const now = moment().tz(userTZ);
  const msgTime = moment(time).tz(userTZ);

  const startOfToday = now.clone().startOf('day');

  if (msgTime.isSameOrAfter(startOfToday)) {
    return msgTime.format('h:mm A');
  } else if (now.year() === msgTime.year()) {
    return msgTime.format('MMM D');
  } else {
    return msgTime.format('MM/DD/YYYY');
  }
}

export const audioErrorHandler = error => {
  switch (error.name) {
    case 'AbortError':
      console.log('An AbortError has occured.');
      break;
    case 'NotAllowedError':
      console.log('A NotAllowedError has occured. User might have denied permission.');
      break;
    case 'NotFoundError':
      console.log('A NotFoundError has occured.');
      break;
    case 'NotReadableError':
      console.log('A NotReadableError has occured.');
      break;
    case 'SecurityError':
      console.log('A SecurityError has occured.');
      break;
    case 'TypeError':
      console.log('A TypeError has occured.');
      break;
    case 'InvalidStateError':
      console.log('An InvalidStateError has occured.');
      break;
    case 'UnknownError':
      console.log('An UnknownError has occured.');
      break;
    default:
      console.log('An error occured with the error name ' + error.name);
  }
};

function isValidURL(url) {
  var urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/;
  return urlPattern.test(url);
}

export function getFileTypeFromUrl(url) {
  if (!isValidURL(url)) {
    return;
  }

  const parsedUrl = new URL(url);

  const pathname = parsedUrl.pathname;
  const extension = pathname.substring(pathname.lastIndexOf('.')).toLowerCase();

  const mimeType = extensionToMimeType(extension);
  return mimeType;
}

export function isAudioMimeType(type) {
  return ['audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'application/octet-stream'].includes(
    String(type || '').toLowerCase()
  );
}

function extensionToMimeType(extension) {
  const extensionToMime = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.mp3': 'audio/mpeg',
    '.m4a': 'audio/mp4',
    '.mp4': 'video/mp4',
    '.pdf': 'application/pdf',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
  };

  return extensionToMime[extension] || 'application/octet-stream';
}

export function formatSeconds(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours.toString().padStart(2, '0')}`);
  }

  parts.push(`${minutes.toString().padStart(2, '0')}`);
  parts.push(`${remainingSeconds.toString().padStart(2, '0')}`);

  return parts.join(':');
}

export function sanitizeKey(inputString) {
  const specialChars = {
    '(': 40,
    ')': 41,
    '/': 47,
    '?': 63,
    '[': 91,
    ']': 93,
    "'": 39,
    ',': 44,
    '.': 46,
  };

  let sanitizedString = '';
  for (let char of inputString) {
    if (specialChars[char] !== undefined) {
      sanitizedString += `_${specialChars[char]}`;
    } else {
      sanitizedString += char;
    }
  }

  return sanitizedString;
}

export function retrieveOriginalValue(sanitizedString) {
  const specialChars = {
    40: '(',
    41: ')',
    47: '/',
    63: '?',
    91: '[',
    93: ']',
    39: "'",
    44: ',',
    46: '.',
  };

  let originalValue = '';
  let i = 0;
  while (i < sanitizedString.length) {
    if (sanitizedString[i] === '_' && i + 3 < sanitizedString.length) {
      const asciiValue = parseInt(sanitizedString.substring(i + 1, i + 4), 10);
      if (!isNaN(asciiValue) && specialChars[asciiValue] !== undefined) {
        originalValue += specialChars[asciiValue];
        i += 4;
        continue;
      }
    }
    originalValue += sanitizedString[i];
    i++;
  }

  return originalValue;
}

export const generateCustomAvatar = (text1, text2) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  context.fillStyle = stringToHslColor(text1);
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.font = 'normal 75px Montserrat_medium';
  context.fillStyle = '#fff';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  let first = typeof text1 === 'string' ? text1.charAt(0).toUpperCase() : undefined;
  let last = typeof text2 === 'string' ? text2.charAt(0).toUpperCase() : undefined;

  if (!last) {
    last = 'U';
  }
  if (!first) {
    first = 'S';
    last = 'U';
  }

  context.fillText(first + last, canvas.width / 2, canvas.height / 2);

  return canvas.toDataURL('image/png');
};

export const stringToHslColor = (str, s = 30, l = 80) => {
  let hash = 0;
  for (let i = 0; i < str?.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let h = hash % 360;
  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
};

function extractFileName(inputString) {
  const parts = inputString.split('-');
  if (parts.length > 0) {
    const fileNameWithExtension = parts[parts.length - 1];
    const fileName = fileNameWithExtension.substring(0, fileNameWithExtension.lastIndexOf('.'));
    return fileName.replace(/-/g, ' ');
  } else {
    return null;
  }
}

export function getFilenameFromPath(filePath) {
  if (!filePath) {
    return;
  }

  const parts = filePath?.split('/');
  const lastPart = parts[parts?.length - 1];
  return extractFileName(lastPart);
}

// Chat attachment URLs are stored as {name}_{timestamp}.{ext} (underscore-
// separated, not hyphen-separated like getFilenameFromPath assumes), so this
// strips query/hash, takes the last path segment, and removes only the
// trailing _<timestamp> immediately before the extension - preserving any
// underscores that are actually part of the original filename.
export function getFilenameFromUrlPath(filePath) {
  if (!filePath) {
    return;
  }

  let filename = filePath.split('?')[0].split('#')[0].split('/').pop();
  if (!filename) {
    return;
  }

  filename = filename.replace(/_\d+(?=\.\w+$)/, '');
  // decodeURIComponent never converts '+' to a space (only %XX sequences),
  // so normalize '+'-encoded spaces before decoding.
  return decodeURIComponent(filename.replace(/\+/g, ' ')).trim();
}

// Truncates the base name (before the extension) to maxLength characters,
// appending an ellipsis and preserving the extension - the extension itself
// is never truncated.
export function truncateFilename(filename, maxLength = 30) {
  if (!filename) return '';

  const lastDot = filename.lastIndexOf('.');
  const hasExt = lastDot > 0 && lastDot < filename.length - 1;
  const base = hasExt ? filename.slice(0, lastDot) : filename;
  const ext = hasExt ? filename.slice(lastDot) : '';

  if (base.length <= maxLength) return filename;

  return `${base.slice(0, maxLength)}…${ext}`;
}

const questionTypes = {
  'Single Choice': 'MatchingRadio',
  'Multi Choice': 'MULTICHECKBOX',
  'Language Select': 'LUNGAGE_SELECT',
  'Drop Down Option': 'DROP_DOWN_OPTION',
  'Drop Down Multi Select': 'SCHOOL_SELECT',
};

export const stepsObjectMaker = ({ data, categorySequence, role }) => {
  let schemaObject = {};
  let formated = data.reduce(
    (acc, cur) => {
      if (!cur?.category) {
        return acc;
      }
      return {
        ...acc,
        res: [
          ...acc.res,
          {
            type: questionTypes[cur.queType],
            label: cur.question,
            key: cur?.queType === 'Drop Down Multi Select' ? cur._id : `${sanitizeKey(cur.question || '')}`,
            required: true,
            options: cur.option || [],
            category: cur.category,
            orderNum: cur.orderNum,
            answer: cur.answer,
            id: cur._id,
            placeholder:
              cur?.queType === 'Drop Down Option'
                ? 'Select'
                : cur?.queType === 'Drop Down Multi Select'
                  ? 'Select School'
                  : null,
          },
        ],
        category: { ...acc.category, [cur.category]: cur.category },
      };
    },
    { res: [], category: {} }
  );
  if (role === 'Mentor') {
    formated = {
      ...formated,
      res: [
        ...formated.res,
        {
          type: 'MatchingRadio',
          label: 'Can you mentor a student in a language other than English?',
          key: `${sanitizeKey('Can you mentor a student in a language other than English?')}`,
          required: true,
          category: 'Personality & Interests',
          orderNum: 4,
          answer: [],
          id: `${sanitizeKey('Can you mentor a student in a language other than English?')}`,
          options: [
            { option: 'Yes', optionNum: 1 },
            { option: 'No', optionNum: 2 },
          ],
          childrens: [
            {
              type: 'LUNGAGE_SELECT',
              label: 'What are the languages (other than English) that you speak comfortably?',
              key: `${sanitizeKey('What are the languages (other than English) that you speak comfortably?')}`,
              required: true,
              options: [],
              category: 'Personality & Interests',
              orderNum: 4,
              answer: [],
              id: '',
            },
          ],
        },
      ],
    };
    // For mentors, always show the "Personality & Interests" category even if it has no matching questions
    // Mentors always must answer the question "Can you mentor a student in a language other than English?" question
    formated.category['Personality & Interests'] = 'Personality & Interests';
  }

  formated = {
    ...formated,
    res: [
      ...formated.res,
      {
        type: 'DESCRIPTION_BOX',
        label:
          "Please tell us a little bit about yourself that we couldn't learn from the previous questions. What else do you think we should know to help find you the best possible match? Tell us what makes you different from everyone else!",
        key: `${sanitizeKey(
          "Please tell us a little bit about yourself that we couldn't learn from the previous questions. What else do you think we should know to help find you the best possible match? Tell us what makes you different from everyone else!"
        )}`,
        required: true,
        wrapperClass: 'w_100',
        placeholder: 'Type your answer here',
        category: 'Education Information',
        orderNum: 4,
        answer: [],
        id: `${sanitizeKey(
          "Please tell us a little bit about yourself that we couldn't learn from the previous questions. What else do you think we should know to help find you the best possible match? Tell us what makes you different from everyone else!"
        )}`,
      },
    ],
  };
  let removedCategory = categorySequence.filter(data => formated.category[data]);

  const result = removedCategory.reduce((acc, cur) => {
    const schema = z
      .object({
        ans: z.string(),
        subAns: z
          .array(z.string(), {
            message: 'Please select a suboption',
          })
          .nullable(),
      })
      .refine(data => data.subAns !== null, {
        message: 'Subanswer cannot be null',
      });
    let response = [
      ...acc,
      {
        title: cur,
        type: 'BASIC_CONTENT',
        childrens: formated.res.reduce((prev, next) => {
          if (next.type !== 'LUNGAGE_SELECT') {
            if (next.id) {
              schemaObject[sanitizeKey(next.id)] =
                next.type === 'MULTICHECKBOX'
                  ? z
                      .object(
                        {
                          ans: z.string(),
                        },
                        { required_error: 'Please select an option' }
                      )
                      .array()
                      .nonempty({
                        message: 'Please select an option',
                      })
                  : next.type === 'DESCRIPTION_BOX'
                    ? z
                        .string({
                          required_error: 'Please enter answer',
                        })
                        .min(1, { message: 'Please enter answer' })
                    : next.type === 'DROP_DOWN_OPTION'
                      ? z
                          .object(
                            {
                              ans: z.string(),
                            },
                            { required_error: 'Please select an option' }
                          )
                          .array()
                          .nonempty({
                            message: 'Please select an option',
                          })
                      : next.type === 'SCHOOL_SELECT'
                        ? z
                            .object(
                              {
                                ans: z.string(),
                              },
                              { required_error: 'Please select atleast one school' }
                            )
                            .array()
                            .nonempty({
                              message: 'Please select atleast one school',
                            })
                        : z
                            .object(
                              {
                                ans: z.string({
                                  required_error: 'Please select an option',
                                }),
                              },
                              {
                                required_error: 'Please select an option',
                              }
                            )
                            .array()
                            .nonempty({
                              message: 'Please select an option',
                            });
            }
          } else {
            schemaObject[sanitizeKey(next.label)] = z
              .string({
                required_error: 'Please select an option',
              })
              .array()
              .nonempty();
          }

          if (next.category === cur) {
            return [
              ...prev,
              {
                type: 'ROW',
                childrens: [next],
              },
            ];
          } else {
            return prev;
          }
        }, []),
      },
    ];
    return response;
  }, []);
  return { result, schemaObject, formatedData: formated.res };
};

export async function blobToMP3(blob) {
  const mp3Blob = new Blob([blob], { type: 'audio/mp3' });
  const mp3File = new File([mp3Blob], 'audio.mp3', {
    type: 'audio/mp3',
  });
  const duration = await getAudioDuration(mp3Blob);

  return { file: mp3File, duration };
}

const getAudioDurationFromMetadata = blob =>
  new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    const objectUrl = URL.createObjectURL(blob);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      audio.removeAttribute('src');
    };

    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      cleanup();
      resolve(Math.floor(audio.duration).toFixed(0));
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error('Unable to read audio duration.'));
    };
    audio.src = objectUrl;
  });

export async function blobToM4A(blob) {
  const m4aBlob = new Blob([blob], { type: 'audio/mp4' });
  const m4aFile = new File([m4aBlob], 'audio.m4a', {
    type: 'audio/mp4',
  });
  const duration = await getAudioDurationFromMetadata(m4aBlob);

  return { file: m4aFile, duration };
}

export const percentage_to_display = percentage => {
  return `${(Number.isNaN(Number(percentage)) ? 0 : Number(percentage)).toFixed(2).replace(/\.00$/, '')} %`;
};

export const routePermission = ['/messages/', '/mentees/', '/progress/', '/home/', '/content/', '/events/'];
export function formatMessageDate(inputDate, timezone) {
  const date = moment(inputDate).tz(timezone?.utc);
  const now = moment().tz(timezone?.utc);
  const today = now.clone().startOf('day');
  const oneYearAgo = now.clone().subtract(1, 'year');

  if (date.isSame(today, 'day')) {
    return `Today at ${date.format('h:mm A')}`;
  } else if (date.isBefore(oneYearAgo)) {
    return `${date.format('M/D/YY')} at ${date.format('h:mm A')}`;
  } else {
    return `${date.format('MMM D')} at ${date.format('h:mm A')}`;
  }
}
export const emojis = {
  Heart: '❤️',
  Smile: '😂',
  Sad: '😞',
  DisBelief: '😅',
  ThumsUp: '👍',
};
export const emojisRegex = /^[a-zA-Z0-9\s!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~\u00C0-\u017F]+$/;

export const UTCToPartnerTimeZone = (date, timezone) => {
  const test = moment.utc(date).tz(timezone?.utc);
  return moment.utc(date).tz(timezone?.utc);
};

export const checkTimeZoneDateEvents = (date, timezone) => {
  let dateTime = new Date(date);

  let convertedTimeforListing = dateTime.toLocaleDateString('en-US', {
    timeZone: timezone?.utc,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
  });
  let convertedTimeforForm = dateTime.toLocaleTimeString('en-US', {
    timeZone: timezone?.utc,
    hour: 'numeric',
    minute: 'numeric',
  });

  return { convertedTimeforListing, convertedTimeforForm };
};

export const UTCToFormattedPartnerTimeZone = (date, timezone) =>
  moment.utc(date).tz(timezone?.utc).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

export function PartnerTimeZoneToUTC(date, timezone) {
  var m = moment.tz(date, 'MM/DD/YYYY h:mm A', timezone?.utc).utc().format('');
  return m;
}

/* Message Utils */

export const MESSAGE_STATUS_ICON = {
  Sent: single_tick,
  Read: Charm_tickdouble,
  Delivered: doubleTick,
};

/**
 * Renders a message receipt icon based on the message's delivery status.
 *
 * @param {boolean} isMyMessage - Whether the message was sent by the current user.
 * @param {{ status: string }} message - The message object containing the delivery status.
 * @returns {JSX.Element|null} An image element representing the message status, or null if
 *   the message is not from the current user or no icon exists for the given status.
 */
export const renderMessageReceipts = (isMyMessage, message) => {
  if (!isMyMessage) return null;

  const icon = MESSAGE_STATUS_ICON[message?.status];
  return icon ? <img src={icon} alt={message.status} /> : null;
};

/**
 * Checks if a chat message is a system message.
 * A system message is not a normal chat bubble from a sender.
 *
 * @param {{ type: string, category: string }} message - The chat message object.
 * @returns {boolean} True if the message is a system message.
 */
export function isSystemChatMessage(message) {
  return message?.type === 'Welcome Message'
    ? true
    : message?.category !== 'Private Prompt' && message?.category !== 'Quote Prompt';
}

const linkifyParts = (text, keyPrefix = '') =>
  text.split(MESSAGE_URL_SPLIT_REGEX).map((part, i) =>
    isMessageURL(part) ? (
      // A bare `www.` link gets the implied scheme on the href only, so the
      // text still renders as authored instead of resolving relative to the
      // app's own origin.
      <a
        style={{ textDecoration: 'underline' }}
        key={`${keyPrefix}${i}`}
        href={normalizeMessageURL(part)}
        target="_blank"
        rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      <span key={`${keyPrefix}${i}`}>{part}</span>
    )
  );

/**
 * Transforms a raw string into a secure JSX tree.
 * This function performs "Linkification" while providing built-in XSS protection.
 * By splitting the string and rendering parts as React children, it ensures
 * that any malicious HTML tags in the input are treated as literal text
 * rather than executable code.
 *
 * IMP-2574: a staff bulk message has `contentFormat: 'basic'` and is rendered with
 * its **bold** and *italic*, still only as React elements. Any other message keeps
 * its asterisks as typed.
 *
 * @param {string} text - The raw message content from the database.
 * @param {string} [contentFormat] - The message's `contentFormat`; 'basic' renders bold and italic.
 * @returns {JSX.Element} A React element tree with escaped text and secure <a> tags.
 */
export const formatMessage = (text, contentFormat) => {
  if (contentFormat !== MessageContentFormat.BASIC) {
    return <>{linkifyParts(text)}</>;
  }

  return (
    <>
      {parseBasicFormat(text).map((span, i) => {
        let content = linkifyParts(span.text, `${i}-`);
        if (span.italic) content = <em>{content}</em>;
        if (span.bold) content = <strong>{content}</strong>;
        return <Fragment key={i}>{content}</Fragment>;
      })}
    </>
  );
};

const getReactionValue = reactionType => {
  let reactions = [
    { name: 'Heart', value: '❤️' },
    { name: 'Smile', value: '😂' },
    { name: 'Sad', value: '😞' },
    { name: 'Disbelief', value: '😅' },
    { name: 'Thumbs Up', value: '👍' },
  ];
  let reaction = reactions.find(reaction => reaction.name.toLowerCase() === reactionType.toLowerCase());
  return reaction ? reaction.value : null;
};

export const formatMessageInWidget = updateConversationMessage => {
  let notification_message = updateConversationMessage?.content ? updateConversationMessage?.content : '';

  if (updateConversationMessage?.messageType === 'Text') {
    notification_message = updateConversationMessage?.content;
  } else if (updateConversationMessage?.messageType === 'Media') {
    const MediaExtensions = ['jpg', 'jpeg', 'png'];

    // Split the URL by dot (.)
    const parts = updateConversationMessage?.file.split('.').pop();

    if (MediaExtensions.includes(parts.toLowerCase())) {
      notification_message = 'Photo';
    }
  } else if (updateConversationMessage?.messageType === 'File') {
    const FileExtensions = ['ppt', 'pptx', 'doc', 'docx', 'pdf', 'csv'];

    // Split the URL by dot (.)
    const parts = updateConversationMessage?.file.split('.').pop();

    if (parts === 'mp3') {
      notification_message = 'Audio';
    } else if (FileExtensions.includes(parts)) {
      notification_message = 'File';
    }
  }

  return `Reacted ${getReactionValue(updateConversationMessage?.reactions[0]?.type)} on “${notification_message?.toLowerCase()}”`;
};
